import {
  Button,
  FormError,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { apiFetch } from "../../api";
import type { Destination, Project } from "../../configTypes";
import { DESTINATION_TYPES } from "../../configTypes";
import { Breadcrumbs } from "./Breadcrumbs";

export function CreateDestination() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId") ?? "";

  const [projectName, setProjectName] = useState<string>("");
  const [name, setName] = useState("");
  const [type, setType] = useState<string>("bigquery-nodedup");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    void apiFetch<Project>(`/api/projects/${projectId}`).then((p) => setProjectName(p.name));
  }, [projectId]);

  const selectedType = DESTINATION_TYPES.find((t) => t.value === type);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const dest = await apiFetch<Destination>("/api/destinations", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), type, projectId }),
      });
      void navigate(`/config/destinations/${dest.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create destination");
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/config/projects" },
          { label: projectName || "...", to: `/config/projects/${projectId}` },
          { label: "New Destination" },
        ]}
      />
      <h2 className="mb-6 text-2xl text-foreground">Create Destination</h2>
      <div className="form-card max-w-[600px]">
        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="flex gap-4">
            <div className="mb-4 flex-1">
              <Label htmlFor="dest-name" className="form-label">
                Name
              </Label>
              <Input
                id="dest-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter value"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- single-purpose route page; focus initial field on mount
                autoFocus
                autoComplete="one-time-code"
              />
            </div>
            <div className="mb-4 flex-1">
              <Label htmlFor="dest-type" className="form-label">
                Type
              </Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger id="dest-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DESTINATION_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {"tooltip" in (selectedType ?? {}) && selectedType && "tooltip" in selectedType && (
            <div className="mb-4 text-xs text-muted-foreground">{selectedType.tooltip}</div>
          )}
          {error && <FormError assertive>{error}</FormError>}
          <div className="form-actions">
            <Button type="button" variant="outline" onClick={() => void navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim()}>
              {submitting ? "Creating..." : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
